import { AlmanacConfig } from "protos/almanac/almanac";
import { BLUE_LOADING_BUTTON } from "portal/theme/theme";
import { buildPermission } from "portal/utils/auth";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import { capitalize, titleCase } from "portal/utils/strings";
import { Field, Form, Formik } from "formik";
import { getFallbackAlmanac } from "portal/utils/almanac";
import { GlobalHotKeys } from "react-hotkeys";
import { object, string } from "yup";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { RobotSummaryResponse } from "../../../../../protos/golang/generated/ts2/portal/robots";
import { Select, TextField } from "formik-mui";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { withAuthorizationRequired } from "../auth/WithAuthorizationRequired";
import AddIcon from "@mui/icons-material/AddOutlined";
import CarbonLogo from "portal/images/logo_icon.svg?react";
import React, { FunctionComponent, useState } from "react";

interface Props {
  hasActiveAlmanacLoaded: boolean;
  hotkey?: string;
  serial?: string;
  basePath: string;
  summary?: RobotSummaryResponse;
  useSetMutation:
    | typeof portalApi.endpoints.setAlmanac.useMutation
    | typeof portalApi.endpoints.setGlobalAlmanac.useMutation;
}
const _NewAlmanac: FunctionComponent<Props> = ({
  hasActiveAlmanacLoaded,
  hotkey,
  serial,
  summary,
  basePath,
  useSetMutation,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [isOpen, setOpen] = useState<boolean>(false);

  const [setAlmanac] = useMutationPopups(useSetMutation(), {
    success: capitalize(
      t("utils.actions.createdLong", {
        subject: t("models.almanacs.almanac_one"),
      })
    ),
  });

  const { data: robotAlmanacs, isSuccess: hasLoadedRobotAlmanacs } =
    useQueryPopups(
      portalApi.endpoints.listAlmanacsForRobot.useQuery(
        serial ? { serial } : skipToken
      )
    );

  const { data: configAdminAlmanacs, isSuccess: hasLoadedAdminAlmanacs } =
    useQueryPopups(portalApi.endpoints.listGlobalAlmanacs.useQuery({ serial }));

  const activeAlmanacId = summary?.robot?.health?.fieldConfig?.activeAlmanacId;

  const dataReady =
    hasLoadedAdminAlmanacs &&
    ((hasActiveAlmanacLoaded && hasLoadedRobotAlmanacs) || !serial);

  const adminAlmanacs =
    dataReady && configAdminAlmanacs.length === 0 && robotAlmanacs?.length === 0
      ? [
          AlmanacConfig.fromPartial({
            ...getFallbackAlmanac(t),
            id: "DEFAULT_FALLBACK_ID",
            name: t("utils.descriptors.default"),
          }),
        ]
      : configAdminAlmanacs ?? [];

  return (
    <>
      {hotkey && (
        <GlobalHotKeys
          keyMap={{
            NEW_ALMANAC: {
              name: titleCase(
                t("utils.actions.newLong", {
                  subject: t("models.almanacs.almanac_one"),
                })
              ),
              action: "keyup",
              sequence: hotkey,
            },
          }}
          handlers={{
            NEW_ALMANAC: () => setOpen(true),
          }}
        />
      )}
      <Button startIcon={<AddIcon />} onClick={() => setOpen(true)}>
        <span className="hidden sm:inline">
          {titleCase(
            t("utils.actions.newLong", {
              subject: t("models.almanacs.almanac_one"),
            })
          )}
        </span>
        <span className="sm:hidden">{t("utils.actions.new")}</span>
      </Button>
      <Dialog open={isOpen} onClose={() => setOpen(false)}>
        <DialogTitle>
          {titleCase(
            t("utils.actions.newLong", {
              subject: t("models.almanacs.almanac_one"),
            })
          )}
        </DialogTitle>

        <Formik
          enableReinitialize
          initialValues={{
            name: "",
            copyFrom:
              robotAlmanacs?.find((a) => a.id === activeAlmanacId)?.id ??
              adminAlmanacs[0]?.id,
          }}
          validationSchema={object({
            name: string().required(),
          })}
          onSubmit={async ({ name, copyFrom }) => {
            if (!serial) {
              return;
            }
            const id = uuid();
            await setAlmanac({
              serial,
              almanac: AlmanacConfig.fromPartial({
                ...[...(robotAlmanacs || []), ...adminAlmanacs].find(
                  (a) => a.id === copyFrom
                ),
                id,
                name,
                protected: false,
                updatedTs: undefined,
              }),
            });
            navigate(`${basePath}/${id}`);
          }}
        >
          {({ submitForm, isSubmitting, dirty }) => (
            <Form>
              <DialogContent className="flex flex-col gap-4 pt-2">
                <Field
                  component={TextField}
                  name="name"
                  label={t("models.almanacs.fields.name")}
                />
                <Field
                  component={Select}
                  name="copyFrom"
                  label={t("utils.form.copyConfigFrom")}
                  className="min-w-48"
                  input={
                    <OutlinedInput
                      disabled={!dataReady}
                      startAdornment={
                        !dataReady && (
                          <InputAdornment position="start">
                            {" "}
                            <CircularProgress
                              className="text-slate-300"
                              size={20}
                            />{" "}
                          </InputAdornment>
                        )
                      }
                      label={t("utils.form.copyConfigFrom")}
                    />
                  }
                >
                  {robotAlmanacs
                    ?.toSorted((a, b) =>
                      a.name.localeCompare(b.name, i18n.language, {
                        numeric: true,
                        sensitivity: "base",
                      })
                    )
                    .flatMap((almanac) => [
                      <MenuItem value={almanac.id} key={almanac.id}>
                        <span>{almanac.name}</span>
                      </MenuItem>,
                    ])}
                  {adminAlmanacs.flatMap((almanac) => [
                    <MenuItem value={almanac.id} key={almanac.id}>
                      <span>
                        <CarbonLogo className="h-3 pr-3" />
                        {almanac.name}
                      </span>
                    </MenuItem>,
                  ])}
                </Field>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  className="text-white"
                  disabled={isSubmitting}
                  onClick={() => setOpen(false)}
                >
                  {t("utils.actions.cancel")}
                </Button>
                <Button
                  {...BLUE_LOADING_BUTTON}
                  disabled={!dirty || !dataReady}
                  loading={isSubmitting}
                  onClick={submitForm}
                  startIcon={<AddIcon />}
                >
                  {t("utils.actions.create")}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export const NewAlmanac = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.update,
        PermissionResource.almanacs,
        PermissionDomain.self
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.almanacs,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.almanacs,
        PermissionDomain.all
      ),
    ],
  },
  _NewAlmanac
);
